import { STREAMING_CURSOR_CHAR } from '@/constants';
import type { ChatMessage } from '@/types/chat';
import { getLatestAssistantMessage } from './getLatestAssistantMessage';

export type ChatThinkingLabelInput = {
  isLoading: boolean;
  isWaitingForDocumentProcessing?: boolean;
  messages: ChatMessage[];
  /** True once no new text has arrived for a moment — see `useIsStreamedTextIdle`. */
  isStreamedTextIdle: boolean;
};

/**
 * Label for the standalone thinking indicator, or `null` to hide it. It only covers the gaps:
 * before the first step, and between the last token and the end of the run. Only the first gap is
 * labelled "Planning"; later phases can't be told apart, so "Thinking".
 */
export const getChatThinkingLabel = ({
  isLoading,
  isWaitingForDocumentProcessing,
  messages,
  isStreamedTextIdle,
}: ChatThinkingLabelInput): string | null => {
  if (!isLoading) return null;
  if (isWaitingForDocumentProcessing) return 'Waiting for document processing';

  const message = getLatestAssistantMessage(messages);
  const steps = message?.processingSteps ?? [];

  // A running step is on screen with a far more specific label than this indicator could give.
  if (steps.some((step) => step.status === 'in_progress')) return null;

  const hasText = Boolean(message?.content?.split(STREAMING_CURSOR_CHAR).join('').trim());

  // Text is still landing, so the response is its own progress indicator. `finalResultReceived`
  // means the text is settled, so only an unfinished response can still be arriving.
  if (hasText && !message?.finalResultReceived && !isStreamedTextIdle) return null;

  // No text and no steps yet — still planning.
  if (!hasText && steps.length === 0) return 'Planning';

  return 'Thinking';
};
